/**
 * How to render a node's reachability, in one place.
 *
 * There are THREE states, not two, and the third one is the reason this file
 * exists. `online` used to be a boolean derived from a clock: a node that had not
 * been heard from in a few minutes was reported offline. For an ESP-NOW router
 * that is fair — it heartbeats through the master, so silence is a missed
 * expectation. For an XBee node it is not: with IO sampling off a node never
 * transmits unprompted, so every healthy node on a quiet mesh went red minutes
 * after the last thing anyone did to it, and its controls greyed out.
 *
 *   online     heard from, or answered something we asked
 *   offline     we asked and it did not answer — an earned claim
 *   unknown    neither, recently — NOT a fault, and must not read as one
 *
 * Every view that draws a dot or writes a word about a node should go through
 * here, so a node cannot be "offline" on the map and "not verified" on its card.
 */

export type NodeState = 'online' | 'offline' | 'unknown';

interface NodeLike {
  online?: boolean;
  state?: NodeState | string | null;
}

/**
 * `state` when the reporting device sends one, otherwise the boolean.
 *
 * The fallback deliberately says `unknown` rather than `offline` for a node that
 * is merely not-online: without a `state` we cannot tell "asked and got nothing"
 * from "nobody asked", and only one of those is a fault worth showing. The server
 * fills `state` in for transports where silence IS evidence, so a genuinely
 * offline ESP-NOW router still arrives here as 'offline'.
 */
export function nodeState(node: NodeLike): NodeState {
  if (node.state === 'online' || node.state === 'offline' || node.state === 'unknown') {
    return node.state;
  }
  return node.online ? 'online' : 'unknown';
}

/** Short label for a badge or a tooltip. */
export function nodeStateLabel(state: NodeState): string {
  return state === 'online' ? 'Online'
    : state === 'offline' ? 'Did not answer'
      : 'Not verified';
}

/** Tailwind background for a status dot. */
export function nodeStateDot(state: NodeState): string {
  return state === 'online' ? 'bg-green-500'
    : state === 'offline' ? 'bg-red-500'
      // Amber, not grey: grey reads as "off" and this is "not sure".
      : 'bg-amber-400';
}

/** The longer form, for a title attribute where there is room to explain. */
export function nodeStateTitle(state: NodeState, noun = 'node'): string {
  return state === 'online'
    ? 'Heard from recently'
    : state === 'offline'
      ? `A command or check was sent to this ${noun} and got no reply`
      : `Nothing heard from this ${noun} recently and nothing has asked it — it has NOT failed`;
}
